/**
 * Shared risk band constants for mentor AI surfaces.
 *
 * These thresholds apply consistently throughout the CRM wherever a risk score is displayed:
 *   - Student Status Dashboard (19-1-b)
 *   - Assessment & Recommendations tab (19-2-b)
 *   - Risk Score Trend Chart (19-3)
 *
 * The thresholds match the backend riskBand filter boundaries.
 */

export const RISK_BAND = {
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
};

/**
 * Return the risk band label for a numeric score 1-10.
 * Returns null when score is null/undefined.
 */
export function getRiskBand(score) {
  if (score == null) return null;
  if (score >= 7) return RISK_BAND.HIGH;
  if (score >= 4) return RISK_BAND.MEDIUM;
  return RISK_BAND.LOW;
}

/**
 * CSS class suffix for a risk band value.
 * Use as `risk-badge--${getRiskBandClass(band)}`.
 */
export function getRiskBandClass(band) {
  switch (band) {
    case RISK_BAND.HIGH: return 'red';
    case RISK_BAND.MEDIUM: return 'amber';
    case RISK_BAND.LOW: return 'green';
    default: return 'grey';
  }
}

/**
 * Human-readable label for a risk band.
 */
export function getRiskBandLabel(band) {
  switch (band) {
    case RISK_BAND.HIGH: return 'High';
    case RISK_BAND.MEDIUM: return 'Medium';
    case RISK_BAND.LOW: return 'Low';
    default: return 'No data';
  }
}

/**
 * Dot color (hex) for chart data points at a given score.
 */
export function getRiskScoreColor(score) {
  if (score == null) return '#9ca3af';
  if (score >= 7) return '#ef4444'; // red
  if (score >= 4) return '#FF9500'; // amber
  return '#22c55e';                  // green
}

/**
 * Trigger label mapping — shared between the trend chart tooltip and the
 * assessment metadata row in 19-2-b. Import from here so both components
 * stay in sync.
 */
export const TRIGGER_LABELS = {
  scheduled_weekly: 'Weekly',
  event_module_completed: 'Module Completed',
  event_course_completed: 'Course Completed',
  event_assignment_graded: 'Assignment Graded',
  event_exam_completed: 'Exam Completed',
  rule_based_detection: 'Rule-Based Detection',
  manual_refresh: 'Manual Refresh',
  course_enrollment: 'Course Enrollment',
  stale_refresh: 'Stale Refresh',
};

/**
 * Convert a raw trigger string to a human-readable label.
 * Falls back to title-casing the raw value if the key is not in the map.
 */
export function getTriggerLabel(trigger) {
  if (!trigger) return 'Unknown';
  if (TRIGGER_LABELS[trigger]) return TRIGGER_LABELS[trigger];
  // Fallback: replace underscores and title-case
  return trigger
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Convert snake_case risk factor strings to sentence-case display text.
 * e.g. "low_quiz_scores" -> "Low quiz scores"
 */
export function formatRiskFactor(factor) {
  if (!factor) return '';
  return factor
    .split('_')
    .map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}
